import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import { createElement } from "@wordpress/element";

/**
 * External dependencies
 */
import classnames from 'classnames';
import { escapeRegExp, find, map, debounce, deburr } from 'lodash';
/**
 * WordPress dependencies
 */

import { Component, renderToString } from '@wordpress/element';
import { ENTER, ESCAPE, UP, DOWN, LEFT, RIGHT, SPACE } from '@wordpress/keycodes';
import { __, _n, sprintf } from '@wordpress/i18n';
import { withInstanceId, compose } from '@wordpress/compose';
import { create, slice, insert, isCollapsed, getTextContent } from '@wordpress/rich-text';
import { getRectangleFromRange } from '@wordpress/dom';
/**
 * Internal dependencies
 */

import withFocusOutside from '../higher-order/with-focus-outside';
import Button from '../button';
import Popover from '../popover';
import withSpokenMessages from '../higher-order/with-spoken-messages';
/**
 * A raw completer option.
 *
 * @typedef {*} CompleterOption
 */

/**
 * @callback FnGetOptions
 *
 * @return {(CompleterOption[]|Promise.<CompleterOption[]>)} The completer options or a promise for them.
 */

/**
 * @callback FnGetOptionKeywords
 * @param {CompleterOption} option a completer option.
 *
 * @return {string[]} list of key words to search.
 */

/**
 * @callback FnIsOptionDisabled
 * @param {CompleterOption} option a completer option.
 *
 * @return {string[]} whether or not the given option is disabled.
 */

/**
 * @callback FnGetOptionLabel
 * @param {CompleterOption} option a completer option.
 *
 * @return {(string|Array.<(string|Component)>)} list of react components to render.
 */

/**
 * @callback FnAllowContext
 * @param {string} before the string before the auto complete trigger and query.
 * @param {string} after  the string after the autocomplete trigger and query.
 *
 * @return {boolean} true if the completer can handle.
 */

/**
 * @typedef {Object} OptionCompletion
 * @property {'insert-at-caret'|'replace'} action the intended placement of the completion.
 * @property {OptionCompletionValue} value the completion value.
 */

/**
 * A completion value.
 *
 * @typedef {(string|WPElement|Object)} OptionCompletionValue
 */

/**
 * @callback FnGetOptionCompletion
 * @param {CompleterOption} value the value of the completer option.
 * @param {string} query the text value of the autocomplete query.
 *
 * @return {(OptionCompletion|OptionCompletionValue)} the completion for the given option. If an
 * 													   OptionCompletionValue is returned, the
 * 													   completion action defaults to `insert-at-caret`.
 */

/**
 * @typedef {Object} Completer
 * @property {string} name a way to identify a completer, useful for selective overriding.
 * @property {?string} className A class to apply to the popup menu.
 * @property {string} triggerPrefix the prefix that will display the menu.
 * @property {(CompleterOption[]|FnGetOptions)} options the completer options or a function to get them.
 * @property {?FnGetOptionKeywords} getOptionKeywords get the keywords for a given option.
 * @property {?FnIsOptionDisabled} isOptionDisabled get whether or not the given option is disabled.
 * @property {FnGetOptionLabel} getOptionLabel get the label for a given option.
 * @property {?FnAllowContext} allowContext filter the context under which the autocomplete activates.
 * @property {FnGetOptionCompletion} getOptionCompletion get the completion associated with a given option.
 */

function filterOptions(search) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var maxResults = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 10;
  var filtered = [];

  for (var i = 0; i < options.length; i++) {
    var option = options[i]; // Merge label into keywords

    var _option$keywords = option.keywords,
        keywords = _option$keywords === void 0 ? [] : _option$keywords;

    if ('string' === typeof option.label) {
      keywords = [].concat(_toConsumableArray(keywords), [option.label]);
    }

    var isMatch = keywords.some(function (keyword) {
      return search.test(deburr(keyword));
    });

    if (!isMatch) {
      continue;
    }

    filtered.push(option); // Abort early if max reached

    if (filtered.length === maxResults) {
      break;
    }
  }

  return filtered;
}

function getCaretRect() {
  var selection = window.getSelection();
  var range = selection.rangeCount ? selection.getRangeAt(0) : null;

  if (range) {
    return getRectangleFromRange(range);
  }
}

export var Autocomplete =
/*#__PURE__*/
function (_Component) {
  _inherits(Autocomplete, _Component);

  _createClass(Autocomplete, null, [{
    key: "getInitialState",
    value: function getInitialState() {
      return {
        search: /./,
        selectedIndex: 0,
        suppress: undefined,
        open: undefined,
        query: undefined,
        filteredOptions: []
      };
    }
  }]);

  function Autocomplete() {
    var _this;

    _classCallCheck(this, Autocomplete);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Autocomplete).apply(this, arguments));
    _this.bindNode = _this.bindNode.bind(_assertThisInitialized(_this));
    _this.select = _this.select.bind(_assertThisInitialized(_this));
    _this.reset = _this.reset.bind(_assertThisInitialized(_this));
    _this.resetWhenSuppressed = _this.resetWhenSuppressed.bind(_assertThisInitialized(_this));
    _this.handleKeyDown = _this.handleKeyDown.bind(_assertThisInitialized(_this));
    _this.debouncedLoadOptions = debounce(_this.loadOptions, 250);
    _this.state = _this.constructor.getInitialState();
    return _this;
  }

  _createClass(Autocomplete, [{
    key: "bindNode",
    value: function bindNode(node) {
      this.node = node;
    }
  }, {
    key: "insertCompletion",
    value: function insertCompletion(replacement) {
      var _this$state = this.state,
          open = _this$state.open,
          query = _this$state.query;
      var _this$props = this.props,
          record = _this$props.record,
          onChange = _this$props.onChange;
      var end = record.start;
      var start = end - open.triggerPrefix.length - query.length;
      var toInsert = create({
        html: renderToString(replacement)
      });
      onChange(insert(record, toInsert, start, end));
    }
  }, {
    key: "select",
    value: function select(option) {
      var onReplace = this.props.onReplace;
      var _this$state2 = this.state,
          open = _this$state2.open,
          query = _this$state2.query;

      var _ref = open || {},
          getOptionCompletion = _ref.getOptionCompletion;

      if (option.isDisabled) {
        return;
      }

      if (getOptionCompletion) {
        var completion = getOptionCompletion(option.value, query);

        var _ref2 = undefined === completion.action || undefined === completion.value ? {
          action: 'insert-at-caret',
          value: completion
        } : completion,
            action = _ref2.action,
            value = _ref2.value;

        if ('replace' === action) {
          onReplace([value]);
        } else if ('insert-at-caret' === action) {
          this.insertCompletion(value);
        }
      } // Reset autocomplete state after insertion rather than before
      // so insertion events don't cause the completion menu to redisplay.


      this.reset();
    }
  }, {
    key: "reset",
    value: function reset() {
      var isMounted = !!this.node; // Autocompletions may replace the block containing this component,
      // so we make sure it is mounted before resetting the state.

      if (isMounted) {
        this.setState(this.constructor.getInitialState());
      }
    }
  }, {
    key: "resetWhenSuppressed",
    value: function resetWhenSuppressed() {
      var _this$state3 = this.state,
          open = _this$state3.open,
          suppress = _this$state3.suppress;

      if (open && suppress === open.idx) {
        this.reset();
      }
    }
  }, {
    key: "handleFocusOutside",
    value: function handleFocusOutside() {
      this.reset();
    }
  }, {
    key: "announce",
    value: function announce(filteredOptions) {
      var debouncedSpeak = this.props.debouncedSpeak;

      if (!debouncedSpeak) {
        return;
      }

      if (!!filteredOptions.length) {
        debouncedSpeak(sprintf(_n('%d result found, use up and down arrow keys to navigate.', '%d results found, use up and down arrow keys to navigate.', filteredOptions.length), filteredOptions.length), 'assertive');
      } else {
        debouncedSpeak(__('No results.'), 'assertive');
      }
    }
    /**
     * Load options for an autocompleter.
     *
     * @param {Completer} completer The autocompleter.
     * @param {string}    query     The query, if any.
     */

  }, {
    key: "loadOptions",
    value: function loadOptions(completer, query) {
      var _this2 = this;

      var options = completer.options;
      /*
       * We support both synchronous and asynchronous retrieval of completer options
       * but internally treat all as async so we maintain a single, consistent code path.
       *
       * Because networks can be slow, and the internet is wonderfully unpredictable,
       * we don't want two promises updating the state at once. This ensures that only
       * the most recent promise will act on `optionsData`. This doesn't use the state
       * because `setState` is batched, and so there's no guarantee that setting
       * `activePromise` in the state would result in it actually being in `this.state`
       * before the promise resolves and we check to see if this is the active promise or not.
       */

      var promise = this.activePromise = Promise.resolve(typeof options === 'function' ? options(query) : options).then(function (optionsData) {
        var _this2$setState;

        if (promise !== _this2.activePromise) {
          // Another promise has become active since this one was asked to resolve, so do nothing,
          // or else we might end triggering a race condition updating the state.
          return;
        }

        var keyedOptions = optionsData.map(function (optionData, optionIndex) {
          return {
            key: "".concat(completer.idx, "-").concat(optionIndex),
            value: optionData,
            label: completer.getOptionLabel(optionData),
            keywords: completer.getOptionKeywords ? completer.getOptionKeywords(optionData) : [],
            isDisabled: completer.isOptionDisabled ? completer.isOptionDisabled(optionData) : false
          };
        });
        var filteredOptions = filterOptions(_this2.state.search, keyedOptions);
        var selectedIndex = filteredOptions.length === _this2.state.filteredOptions.length ? _this2.state.selectedIndex : 0;

        _this2.setState((_this2$setState = {}, _defineProperty(_this2$setState, 'options_' + completer.idx, keyedOptions), _defineProperty(_this2$setState, "filteredOptions", filteredOptions), _defineProperty(_this2$setState, "selectedIndex", selectedIndex), _this2$setState));

        _this2.announce(filteredOptions);
      });
    }
  }, {
    key: "handleKeyDown",
    value: function handleKeyDown(event) {
      var _this$state4 = this.state,
          open = _this$state4.open,
          suppress = _this$state4.suppress,
          selectedIndex = _this$state4.selectedIndex,
          filteredOptions = _this$state4.filteredOptions;

      if (!open) {
        return;
      }

      if (suppress === open.idx) {
        switch (event.keyCode) {
          // cancel popup suppression on CTRL+SPACE
          case SPACE:
            var ctrlKey = event.ctrlKey,
                shiftKey = event.shiftKey,
                altKey = event.altKey,
                metaKey = event.metaKey;

            if (ctrlKey && !(shiftKey || altKey || metaKey)) {
              this.setState({
                suppress: undefined
              });
              event.preventDefault();
              event.stopPropagation();
            }

            break;
          // reset on cursor movement

          case UP:
          case DOWN:
          case LEFT:
          case RIGHT:
            this.reset();
        }

        return;
      }

      if (filteredOptions.length === 0) {
        return;
      }

      var nextSelectedIndex;

      switch (event.keyCode) {
        case UP:
          nextSelectedIndex = (selectedIndex === 0 ? filteredOptions.length : selectedIndex) - 1;
          this.setState({
            selectedIndex: nextSelectedIndex
          });
          break;

        case DOWN:
          nextSelectedIndex = (selectedIndex + 1) % filteredOptions.length;
          this.setState({
            selectedIndex: nextSelectedIndex
          });
          break;

        case ESCAPE:
          this.setState({
            suppress: open.idx
          });
          break;

        case ENTER:
          this.select(filteredOptions[selectedIndex]);
          break;

        case LEFT:
        case RIGHT:
          this.reset();
          return;

        default:
          return;
      } // Any handled keycode should prevent original behavior. This relies on
      // the early return in the default case.


      event.preventDefault();
      event.stopPropagation();
    }
  }, {
    key: "toggleKeyEvents",
    value: function toggleKeyEvents(isListening) {
      // This exists because we must capture ENTER key presses before RichText.
      // It seems that react fires the simulated capturing events after the
      // native browser event has already bubbled so we can't stopPropagation
      // and avoid RichText getting the event from TinyMCE, hence we must
      // register a native event handler.
      var handler = isListening ? 'addEventListener' : 'removeEventListener';
      this.node[handler]('keydown', this.handleKeyDown, true);
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState) {
      var _this$props2 = this.props,
          record = _this$props2.record,
          completers = _this$props2.completers;
      var prevRecord = prevProps.record;
      var prevOpen = prevState.open;

      if (!this.state.open !== !prevOpen) {
        this.toggleKeyEvents(!!this.state.open);
      }

      if (isCollapsed(record)) {
        var text = deburr(getTextContent(slice(record, 0)));
        var prevText = deburr(getTextContent(slice(prevRecord, 0)));

        if (text !== prevText) {
          var textAfterSelection = getTextContent(slice(record, undefined, getTextContent(record).length));
          var allCompleters = map(completers, function (completer, idx) {
            return _objectSpread({}, completer, {
              idx: idx
            });
          });
          var open = find(allCompleters, function (_ref3) {
            var triggerPrefix = _ref3.triggerPrefix,
                allowContext = _ref3.allowContext;
            var index = text.lastIndexOf(triggerPrefix);

            if (index === -1) {
              return false;
            }

            if (allowContext && !allowContext(text.slice(0, index), textAfterSelection)) {
              return false;
            }

            return /^\S*$/.test(text.slice(index + triggerPrefix.length));
          });

          if (!open) {
            this.reset();
            return;
          }

          var safeTrigger = escapeRegExp(open.triggerPrefix);
          var match = text.match(new RegExp("".concat(safeTrigger, "(\\S*)$")));
          var query = match && match[1];
          var _this$state5 = this.state,
              wasOpen = _this$state5.open,
              wasSuppress = _this$state5.suppress,
              wasQuery = _this$state5.query;

          if (open && (!wasOpen || open.idx !== wasOpen.idx || query !== wasQuery)) {
            if (open.isDebounced) {
              this.debouncedLoadOptions(open, query);
            } else {
              this.loadOptions(open, query);
            }
          } // create a regular expression to filter the options


          var search = open ? new RegExp('(?:\\b|\\s|^)' + escapeRegExp(query), 'i') : /./; // filter the options we already have

          var filteredOptions = open ? filterOptions(search, this.state['options_' + open.idx]) : []; // check if we should still suppress the popover

          var suppress = open && wasSuppress === open.idx ? wasSuppress : undefined; // update the state

          if (wasOpen || open) {
            this.setState({
              selectedIndex: 0,
              filteredOptions: filteredOptions,
              suppress: suppress,
              search: search,
              open: open,
              query: query
            });
          } // announce the count of filtered options but only if they have loaded


          if (open && this.state['options_' + open.idx]) {
            this.announce(filteredOptions);
          }
        }
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.toggleKeyEvents(false);
      this.debouncedLoadOptions.cancel();
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props3 = this.props,
          children = _this$props3.children,
          instanceId = _this$props3.instanceId;
      var _this$state6 = this.state,
          open = _this$state6.open,
          suppress = _this$state6.suppress,
          selectedIndex = _this$state6.selectedIndex,
          filteredOptions = _this$state6.filteredOptions;

      var _ref4 = filteredOptions[selectedIndex] || {},
          _ref4$key = _ref4.key,
          selectedKey = _ref4$key === void 0 ? '' : _ref4$key;

      var _ref5 = open || {},
          className = _ref5.className,
          idx = _ref5.idx;

      var isExpanded = suppress !== idx && filteredOptions.length > 0;
      var listBoxId = isExpanded ? "components-autocomplete-listbox-".concat(instanceId) : null;
      var activeId = isExpanded ? "components-autocomplete-item-".concat(instanceId, "-").concat(selectedKey) : null; // Disable reason: Clicking the editor should reset the autocomplete when the menu is suppressed

      /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */

      return createElement("div", {
        ref: this.bindNode,
        onClick: this.resetWhenSuppressed,
        className: "components-autocomplete"
      }, children({
        isExpanded: isExpanded,
        listBoxId: listBoxId,
        activeId: activeId
      }), isExpanded && createElement(Popover, {
        focusOnMount: false,
        onClose: this.reset,
        position: "top right",
        className: "components-autocomplete__popover",
        getAnchorRect: getCaretRect
      }, createElement("div", {
        id: listBoxId,
        role: "listbox",
        className: "components-autocomplete__results"
      }, isExpanded && map(filteredOptions, function (option, index) {
        return createElement(Button, {
          key: option.key,
          id: "components-autocomplete-item-".concat(instanceId, "-").concat(option.key),
          role: "option",
          "aria-selected": index === selectedIndex,
          disabled: option.isDisabled,
          className: classnames('components-autocomplete__result', className, {
            'is-selected': index === selectedIndex
          }),
          onClick: function onClick() {
            return _this3.select(option);
          }
        }, option.label);
      }))));
      /* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    }
  }]);

  return Autocomplete;
}(Component);
export default compose([withSpokenMessages, withInstanceId, withFocusOutside])(Autocomplete);
//# sourceMappingURL=index.js.map